import { useCallback, useEffect, useRef } from 'react';
import type React from 'react';
import ReactDOM from 'react-dom';
import FocusLock from 'react-focus-lock';
import { getGlobalLogger } from '@zg-rentals/logger-base';

import omit from 'lodash/omit';
import ValueConstants from 'app/shared/constants/ValueConstants';
import IconX from 'images/icons/x.svg';
import IconXWhiteBold from 'images/icons/x-white-bold.svg';

import * as S from 'app/shared/modules/popups/PopupModal/styles';
import type { ModalConfig } from '../types';

const logger = getGlobalLogger('contexts/ModalContext/PopupModal');

type ModalSize = 'auto' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl' | '320' | '420' | '480' | '640' | '800';
type PaddingSize = '0' | 'xs' | '1x' | '2x' | '3x' | '4x' | '6x' | '8x';
type PaddingInlineSize = 'xs' | '1x' | '2x' | '3x' | '4x' | '6x' | '8x';
type ModalPosition = 'top';

interface AriaAttributes {
  'aria-label'?: string;
  'aria-labelledby'?: string;
  'aria-describedby'?: string;
  'aria-modal'?: boolean;
}

interface OverlayProps extends AriaAttributes {
  role: 'dialog';
  onClick?: () => void; // Made optional to support webview modals without backdrop click
  darkOverlay?: boolean;
  transparentOverlay?: boolean;
  style?: React.CSSProperties;
}

export interface ModalProviderPopupModalProps {
  /**
   * Modal configuration from ModalProvider
   */
  modalConfig: ModalConfig;

  /**
   * Modal component to render
   */
  children: React.ReactNode;

  /**
   * Handler to close this specific modal
   */
  onClose: () => void;

  /**
   * Custom close icon (default is dark X)
   */
  customCloseIcon?: React.ReactNode;

  /**
   * Uses dark gray transparent overlay instead
   */
  darkOverlay?: boolean;

  /**
   * Uses completely transparent overlay (for nested modals)
   */
  transparentOverlay?: boolean;

  /**
   * Specifies that modal should be full height of screen
   */
  fullHeight?: boolean;

  /**
   * Floats close button on overlay instead of modal
   */
  floatingCloseButton?: boolean;

  /**
   * Flag to remove overlay
   */
  isFullScreen?: boolean;

  /**
   * Flag to disable ESC key functionality in webview environments
   */
  isWebview?: boolean;

  /**
   * ID of HTML element to append portal to
   */
  modalId?: string;

  /**
   * String to set padding-bottom
   */
  paddingBottom?: PaddingSize;

  /**
   * String to set padding-inline
   */
  paddingInline?: PaddingInlineSize;

  /**
   * String to set padding-top
   */
  paddingTop?: PaddingSize;

  /**
   * String to set position of modal (default middle of screen)
   */
  position?: ModalPosition;

  /**
   * Override for components that have their own scroll container
   */
  scrollContainerOverride?: boolean;

  /**
   * String to set padding-inline for scrollable content
   */
  scrollablePaddingInline?: PaddingInlineSize;

  /**
   * Specifies size of modal
   */
  size?: ModalSize;

  /**
   * Flag to animate modal to slide up from bottom
   */
  slideUp?: boolean;

  /**
   * Flag to show close button
   */
  useCloseButton?: boolean;

  /**
   * Flag to remove the header padding (48px top space) but keep close button
   * Useful for mobile/native apps where you want the close button without padding
   */
  hideHeader?: boolean;

  /**
   * Flag to hide the close button entirely
   * Useful for native app contexts where the native app handles modal dismissal
   */
  hideCloseButton?: boolean;

  /**
   * Flag to hide the modal footer area
   */
  hideFooter?: boolean;

  /**
   * Additional class name
   */
  className?: string;

  /**
   * Modal label for accessibility (required for screen readers)
   */
  ariaLabel?: string;

  /**
   * ID of the element that describes the modal for accessibility
   */
  ariaDescribedBy?: string;

  /**
   * ID of the element that labels the modal for accessibility
   */
  ariaLabelledBy?: string;

  /**
   * Whether this modal should handle ESC key presses (only highest priority modal should)
   */
  shouldHandleEscKey?: boolean;

  /**
   * Whether this modal should receive initial focus (only highest priority modal should)
   */
  shouldReceiveInitialFocus?: boolean;
}

/**
 * PopupModal for ModalProvider system
 * This component is specifically designed to work with the ModalProvider context
 * and renders individual modals based on ModalConfig.
 */
const PopupModal: React.FC<ModalProviderPopupModalProps> = ({
  modalConfig,
  ariaDescribedBy,
  ariaLabel,
  ariaLabelledBy,
  children,
  className,
  customCloseIcon,
  darkOverlay = false,
  transparentOverlay = false,
  floatingCloseButton = false,
  fullHeight = false,
  isFullScreen,
  isWebview = false,
  modalId = 'modal-root',
  onClose,
  paddingBottom = '4x',
  paddingInline,
  paddingTop = '6x',
  position,
  scrollablePaddingInline,
  scrollContainerOverride,
  size = 'auto',
  slideUp = false,
  useCloseButton = true,
  hideHeader = false,
  hideCloseButton = false,
  hideFooter = false,
  shouldHandleEscKey = true,
  shouldReceiveInitialFocus = true,
  ...additionalProps
}) => {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const portalElementRef = useRef<HTMLElement | null>(null);

  // Set up screen reader announcement (only for the highest priority modal)
  // ESC key handling is now centralized in ModalProvider to prevent multiple listeners
  useEffect(() => {
    if (typeof window !== 'undefined' && 'screen' in window && shouldReceiveInitialFocus) {
      let screenReaderAlert: HTMLDivElement | null = null;
      try {
        screenReaderAlert = document.createElement('div');
        screenReaderAlert.setAttribute('role', 'status');
        screenReaderAlert.setAttribute('aria-live', 'polite');
        screenReaderAlert.className = 'sr-only';
        screenReaderAlert.textContent = `Modal opened: ${modalConfig.type}`;
        document.body.appendChild(screenReaderAlert);
      } catch (error) {
        logger?.warn('Failed to create screen reader announcement:', error);
      }

      return () => {
        try {
          if (screenReaderAlert && document.body.contains(screenReaderAlert)) {
            document.body.removeChild(screenReaderAlert);
          }
        } catch (error) {
          logger?.warn('Failed to remove screen reader announcement:', error);
        }
      };
    }
  }, [modalConfig.type, shouldReceiveInitialFocus]);

  useEffect(() => {
    if (buttonRef.current && shouldReceiveInitialFocus) {
      buttonRef.current.focus();
    }
  }, [shouldReceiveInitialFocus]);

  const handleModalClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  const rest = omit(additionalProps, [
    'children',
    'customCloseIcon',
    'className',
    'dispatch',
    'modalId',
    'onClose',
    'paddingBottom',
    'paddingInline',
    'paddingTop',
    'position',
    'scrollablePaddingInline',
    'slideUp',
    'useCloseButton',
    'hideHeader',
    'hideCloseButton',
    'hideFooter',
    'ariaLabel',
    'ariaDescribedBy',
    'ariaLabelledBy',
    'isWebview',
    'modalConfig',
    'shouldHandleEscKey',
    'shouldReceiveInitialFocus',
  ]);

  // Check if this is a webview modal (with fallback URL detection for legacy system)
  let isWebviewModal = isWebview;
  if (!isWebviewModal && typeof window !== 'undefined') {
    const urlParams = new URLSearchParams(window.location.search);
    isWebviewModal = urlParams.has('webviewModal');
  }

  const overlayProps: OverlayProps = {
    role: 'dialog',
    'aria-modal': true,
    onClick: isWebviewModal ? undefined : onClose, // Disable backdrop click for webview modals
    darkOverlay: darkOverlay ? true : undefined,
    transparentOverlay: transparentOverlay ? true : undefined,
    style: { zIndex: 1000 + (modalConfig.priority ?? 1) },
  };

  if (ariaLabel) {
    overlayProps['aria-label'] = ariaLabel;
  }

  if (ariaLabelledBy) {
    overlayProps['aria-labelledby'] = ariaLabelledBy;
  }

  if (ariaDescribedBy) {
    overlayProps['aria-describedby'] = ariaDescribedBy;
  }

  const contentProps = {
    ref: contentRef,
    className,
    position,
    slideUp: slideUp ? true : undefined,
    isFullScreen: isFullScreen ? true : undefined,
    fullHeight: fullHeight ? true : undefined,
    size,
    floatingCloseButton: floatingCloseButton ? true : undefined,
    onClick: handleModalClick,
    tabIndex: -1,
    ...rest,
  };

  const scrollableContentPaddingTop = hideHeader ? '0' : !useCloseButton ? '2x' : paddingTop;
  const scrollableContentPaddingBottom = hideFooter || !useCloseButton ? '1x' : paddingBottom;

  const closeButtonProps = {
    ref: buttonRef,
    isFullScreen: isFullScreen ? true : undefined,
    floatingCloseButton: floatingCloseButton ? true : undefined,
    scrollablePaddingInline: !floatingCloseButton ? scrollablePaddingInline : undefined,
    onClick: onClose,
    'aria-label': `Close ${modalConfig.type} modal`,
  };

  const Content = (
    <FocusLock autoFocus={false} returnFocus>
      <S.PopupModalOverlay {...overlayProps}>
        <S.PopupModalContent {...contentProps}>
          {!hideCloseButton && useCloseButton && (
            <S.PopupModalCloseButton {...closeButtonProps}>
              {customCloseIcon ? (
                customCloseIcon
              ) : (
                <img
                  src={darkOverlay && floatingCloseButton ? IconXWhiteBold : IconX}
                  height={darkOverlay ? '14px' : '16px'}
                  width={darkOverlay ? '14px' : '16px'}
                  alt=""
                />
              )}
            </S.PopupModalCloseButton>
          )}
          {scrollContainerOverride ? (
            <>{children}</>
          ) : (
            <S.ScrollableContent
              id="modal-scroll-container"
              paddingTop={scrollableContentPaddingTop}
              paddingBottom={scrollableContentPaddingBottom}
              paddingInline={scrollablePaddingInline}
            >
              {children}
            </S.ScrollableContent>
          )}
        </S.PopupModalContent>
      </S.PopupModalOverlay>
    </FocusLock>
  );

  /**
   * Renders content inline on SSR, then renders content in a portal `modal-root` once the client
   * and document is loaded. This will allow us to have content still appear in HTML on SSR, which
   * is important if we use modals for SSR critical content, like HDPs
   *
   * Portal element is cached to avoid repeated DOM queries for performance
   */
  if (typeof document !== 'undefined') {
    try {
      if (!portalElementRef.current) {
        portalElementRef.current = document.getElementById(modalId);
      }

      const modalElement = portalElementRef.current;
      if (!modalElement) {
        logger?.warn(`Modal portal target element with id "${modalId}" not found. Rendering inline.`);
        return Content;
      }
      return ReactDOM.createPortal(Content, modalElement);
    } catch (error) {
      logger?.warn(`Failed to create portal for modal "${modalConfig.type}":`, error);
      return Content;
    }
  } else {
    return Content;
  }
};

export default PopupModal;
